/**
 * Report Database — IndexedDB wrapper for MMM report storage
 *
 * Replaces the localStorage-based report library with IndexedDB to handle
 * larger HTML payloads (localStorage caps at ~5MB per origin, while a single
 * MMM report with inline AI summary can be 50-100KB).
 *
 * Migration: on first open, existing localStorage reports are imported
 * automatically so users don't lose their library.
 *
 * Issue: YDMA-179 §3.3
 */

import { logError } from './logger';

/**
 * Base report shape stored in IndexedDB.
 * Both MMM and Performance reports satisfy this interface.
 * The `context` field is opaque (unknown) so the DB layer
 * doesn't need to know about specific report context types.
 */
export interface BaseReport {
  id: string;
  /**
   * Which workspace the report's figures describe.
   *
   * Optional only so records written before scoping existed still parse; they
   * are treated as belonging to no workspace and are hidden from every library,
   * because a report whose client cannot be identified is worse than a missing
   * one — an agency reading it in the wrong tab would present the wrong client's
   * spend.
   */
  workspaceId?: string;
  title: string;
  scenario: string;
  createdAt: string;
  html: string;
  summary: string;
  context: unknown;
}

// ============================================================
// CONSTANTS
// ============================================================

const DB_NAME = 'ydm_mi_reports';
const DB_VERSION = 1;
const STORE_NAME = 'reports';
const LEGACY_LS_KEY = 'ydm_mmm_reports';
/**
 * Cap per WORKSPACE, not per browser.
 *
 * It counted the whole store, so an operator filling one client's library
 * deleted a different client's older reports — in a workspace they were not
 * looking at, with nothing on screen to say so. The read side has been
 * workspace-scoped since e102745; this is the other half of it.
 *
 * Fifty each is affordable: measured on a real profile, the browser offered
 * ~10 GB of storage, so even ten workspaces at the cap is a rounding error
 * against it. The number exists to stop one library growing without bound,
 * not to ration space between clients.
 */
export const MAX_REPORTS = 50;

// ============================================================
// DB OPEN + MIGRATION
// ============================================================

let _db: IDBDatabase | null = null;
let _openPromise: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
  if (_db) return Promise.resolve(_db);
  if (_openPromise) return _openPromise;

  _openPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        // Reads no longer go through this index (see getAllReportsUnscoped), but
        // it is still created: every browser that already opened this database at
        // version 1 has it, and dropping it here without bumping DB_VERSION would
        // leave fresh and existing installs on different schemas.
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };

    request.onsuccess = async () => {
      _db = request.result;

      // Migrate existing localStorage reports on first open
      try {
        await migrateFromLocalStorage(_db);
      } catch {
        // non-critical — proceed without migration
      }

      resolve(_db);
    };

    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB open failed'));
    };
  });

  return _openPromise;
};

/**
 * Imports existing localStorage reports into IndexedDB (one-time migration).
 * Silently skips if localStorage is empty or already migrated.
 *
 * Only rows that name a workspace are imported. A report with no workspaceId is
 * hidden from every library by design (see BaseReport), so importing one adds a
 * row nobody can open, read or delete — and the 50-report cap counts rows, not
 * readable ones, so the next save pays for that slot by pruning a report the
 * reader can actually see. Rows are normalised on the way in for the same
 * reason the read path normalises on the way out: what cannot be read back is
 * not worth storing, and a legacy entry with no id would otherwise abort the
 * whole import with a DataError.
 *
 * What is skipped stays in localStorage. Unreadable here is not the same as
 * worthless — that copy is the only one left — and rewriting the key to hold
 * exactly the leftovers is also what stops a second open from re-importing, and
 * so resurrecting, reports the reader has since deleted.
 */
const migrateFromLocalStorage = async (db: IDBDatabase): Promise<void> => {
  const raw = localStorage.getItem(LEGACY_LS_KEY);
  if (!raw) return;

  let legacyReports: unknown;
  try {
    legacyReports = JSON.parse(raw);
  } catch {
    return; // corrupted — skip migration
  }

  if (!Array.isArray(legacyReports) || legacyReports.length === 0) return;

  const importable: BaseReport[] = [];
  const skipped: unknown[] = [];
  for (const legacy of legacyReports) {
    const report = toBaseReport(legacy);
    if (report?.workspaceId) importable.push(report);
    else skipped.push(legacy);
  }

  if (importable.length === 0) return; // nothing this store could hand back

  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  for (const report of importable) {
    store.put(report);
  }

  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });

  // Clear only what the transaction actually took, after it committed.
  if (skipped.length > 0) localStorage.setItem(LEGACY_LS_KEY, JSON.stringify(skipped));
  else localStorage.removeItem(LEGACY_LS_KEY);
};

// ============================================================
// CRUD OPERATIONS
// ============================================================

/**
 * Reports belonging to one workspace, newest first.
 *
 * The scope argument is mandatory. This used to return every report in the
 * browser regardless of which workspace was open, and nothing on the card said
 * which client a report described — so an agency with several clients saw them
 * interleaved, and opening the wrong one meant presenting another client's
 * budget. Records saved before scoping carry no workspaceId and are excluded.
 */
export const getReportsForWorkspace = async (workspaceId: string): Promise<BaseReport[]> => {
  const all = await getAllReportsUnscoped();
  return all.filter((r) => r.workspaceId === workspaceId);
};

/**
 * Turns a stored record into a BaseReport, or discards it.
 *
 * IndexedDB is not typed: this store keeps whatever any version of the app ever
 * wrote into it, and it survives deploys, so a row can be missing a field that
 * BaseReport declares as required. The read path used to say `cursor.value as
 * BaseReport`, which is an assertion rather than a check — a row saved without a
 * summary reached the library as if it had one, and `report.summary.replace(...)`
 * threw during render. Because that throw happens inside the route's
 * ErrorBoundary, the whole page went to the fallback: one malformed row and the
 * reader loses every other report they saved.
 *
 * Missing text becomes an empty string, which every consumer already handles
 * (searching skips it, the card renders no preview). A row with no usable id is
 * dropped instead — it cannot be opened, deleted, or told apart from another
 * one, so listing it only offers controls that cannot work.
 */
export const toBaseReport = (raw: unknown): BaseReport | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id) return null;

  const text = (v: unknown): string => (typeof v === 'string' ? v : '');
  return {
    id: r.id,
    workspaceId: typeof r.workspaceId === 'string' ? r.workspaceId : undefined,
    title: text(r.title),
    scenario: text(r.scenario),
    createdAt: text(r.createdAt),
    html: text(r.html),
    summary: text(r.summary),
    context: r.context,
  };
};

const descending = (a: string, b: string): number => (a < b ? 1 : a > b ? -1 : 0);

/**
 * Every report in the browser, whatever workspace it belongs to.
 *
 * Internal: the pruning and migration paths need it. UI code must use
 * getReportsForWorkspace so a report cannot surface under the wrong client.
 *
 * Reads the store itself and orders in memory rather than walking the createdAt
 * index. IndexedDB writes no index entry for a record whose key path holds
 * nothing, so a row saved without a createdAt was in the store but in none of
 * these results: invisible to every library, and — because the prune walks this
 * same list — impossible to delete as well, holding storage nobody could
 * reclaim. toBaseReport has always been written to hand such a row back with an
 * empty date; now it gets the chance to.
 */
const getAllReportsUnscoped = async (): Promise<BaseReport[]> => {
  try {
    const db = await openDb();
    const stored = await new Promise<unknown[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result ?? []);
      request.onerror = () => reject(request.error);
    });

    const reports = stored
      .map((row) => toBaseReport(row))
      .filter((report): report is BaseReport => report !== null);

    // Newest first. An undated report sorts last, which is where the index put a
    // createdAt of '' and the only honest place for one of unknown age: the
    // prune deletes from the tail, so the alternative is discarding a report
    // whose date we do know. id breaks ties so two reports saved in the same
    // millisecond keep one order between reads — an order that shuffled would
    // let the prune delete a different report on every save.
    return reports.sort(
      (a, b) => descending(a.createdAt, b.createdAt) || descending(a.id, b.id)
    );
  } catch (err) {
    logError('ReportDb', 'getAllReports failed', err);
    return [];
  }
};

/**
 * Saves a report (insert or update by id).
 * Enforces MAX_REPORTS for the saved report's OWN workspace, deleting that
 * workspace's oldest excess. A save can never delete another client's report.
 */
export const putReport = async (report: BaseReport): Promise<void> => {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put(report);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });

    // Trim this workspace's library only — never another client's.
    await enforceMaxLimit(report.workspaceId);
  } catch (err) {
    logError('ReportDb', 'putReport failed', err);
  }
};

/**
 * Deletes a report by id.
 */
export const deleteReportById = async (reportId: string): Promise<void> => {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(reportId);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    logError('ReportDb', 'deleteReport failed', err);
  }
};

/**
 * Deletes oldest reports beyond MAX_REPORTS.
 */
const enforceMaxLimit = async (workspaceId: string | undefined): Promise<void> => {
  try {
    const db = await openDb();
    const all = await getAllReportsUnscoped();
    // Only this workspace's own reports are candidates. Reports belonging to
    // another client are counted by no cap but this one, and deleted by none.
    const mine = all.filter((r) => r.workspaceId === workspaceId);
    if (mine.length <= MAX_REPORTS) return;

    const excess = mine.slice(MAX_REPORTS); // oldest of THIS workspace's, beyond the cap
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      for (const report of excess) {
        store.delete(report.id);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // non-critical
  }
};

// ============================================================
// SEARCH
// ============================================================

/**
 * Searches reports by title, scenario, or summary text.
 */
export const searchReportsDb = async (
  query: string,
  workspaceId: string,
): Promise<BaseReport[]> => {
  // Scope first, then match. Searching across every workspace and filtering the
  // results by text would still surface another client's report to anyone who
  // typed a word it happened to contain.
  const scoped = await getReportsForWorkspace(workspaceId);
  if (!query.trim()) return scoped;

  const q = query.toLowerCase();
  return scoped.filter(
    (r) =>
      r.title.toLowerCase().includes(q) ||
      r.scenario.toLowerCase().includes(q) ||
      r.summary.toLowerCase().includes(q)
  );
};

export default {
  getReportsForWorkspace,
  putReport,
  deleteReportById,
  searchReportsDb,
};
